import { EventTypes, TYPES } from '../../render/constants';
import { createAlignCache, createAlignResult } from '../align';
import { Rect } from '../region';

// 判定父容器是否支持本功能（v1：frame/col/group）
function isSupportedParent(node) {
  if (!node) return false;
  return node.type === TYPES.frame || node.type === TYPES.col || node.type === TYPES.group;
}

// 仅底部中点的 sectionMove 句柄
export function getSectionMoveHandlers(stageContext, node, region, scale = 1) {
  const selected = stageContext.api.getSelectedNodes() || [];
  const isSelectionRegion = !node || !node.id || region?.type === 'selection';

  // 多选：仅支持同父容器
  if (isSelectionRegion) {
    if (selected.length < 2) return [];
    const parentId = selected[0]?.parentId;
    if (!parentId) return [];
    const sameParent = selected.every((n) => n.parentId === parentId);
    if (!sameParent) return [];
    const parent = stageContext.api.getNode(parentId);
    if (!isSupportedParent(parent)) return [];
    const children = stageContext.api.getChildren(parentId) || [];
    const siblings = children.filter((n) => !selected.find((s) => s.id === n.id));
    if (!siblings.length) return [];

    const bottomMid = {
      x: (region.bottomLeft.x + region.bottomRight.x) / 2,
      y: (region.bottomLeft.y + region.bottomRight.y) / 2,
    };
    const center = region.center;
    const vx = bottomMid.x - center.x;
    const vy = bottomMid.y - center.y;
    const len = Math.hypot(vx, vy) || 1;
    const nx = vx / len;
    const ny = vy / len;

    const outwardGap = 56 * scale;
    const hitRadius = 28 * scale;
    const visualRadius = 20 * scale;
    let hx = bottomMid.x + nx * outwardGap;
    let hy = bottomMid.y + ny * outwardGap;

    const tx = -ny,
      ty = nx; // 切线方向
    const lateral = 48 * scale;
    hx += tx * lateral;
    hy += ty * lateral;

    const hit = new Rect(hx - hitRadius, hy - hitRadius, hitRadius * 2, hitRadius * 2);
    hit.id = 'sectionMove';
    hit.type = 'sectionMove';
    hit.node = null;
    hit.nodes = region.nodes;
    hit.visualRadius = visualRadius;
    hit.mode = 'selection';
    hit.selectedIds = selected.map((n) => n.id);
    hit.parentId = parentId;
    hit.selectionRect = region.rect ? { ...region.rect } : null;
    return [hit];
  }

  // 单选
  if (!selected || selected.length !== 1 || selected[0].id !== node.id) return [];
  if (stageContext.api.shouldSkip(node)) return [];

  const parent = stageContext.api.getNode(node.parentId);
  if (!isSupportedParent(parent)) return [];

  const siblings = (stageContext.api.getChildren(parent?.id) || []).filter((n) => n.id !== node.id);
  if (!siblings.length) return [];

  const bottomMid = {
    x: (region.bottomLeft.x + region.bottomRight.x) / 2,
    y: (region.bottomLeft.y + region.bottomRight.y) / 2,
  };
  const center = region.center;
  const vx = bottomMid.x - center.x;
  const vy = bottomMid.y - center.y;
  const len = Math.hypot(vx, vy) || 1;
  const nx = vx / len;
  const ny = vy / len;

  const outwardGap = 56 * scale;
  const hitRadius = 28 * scale;
  const visualRadius = 20 * scale;

  const hx = bottomMid.x + nx * outwardGap;
  const hy = bottomMid.y + ny * outwardGap;

  const hit = new Rect(hx - hitRadius, hy - hitRadius, hitRadius * 2, hitRadius * 2);
  hit.id = 'sectionMove';
  hit.type = 'sectionMove';
  hit.node = node;
  hit.visualRadius = visualRadius;
  hit.mode = 'single';

  return [hit];
}

// 句柄渲染（小型紫色胶囊）
export function renderSectionMoveHandler(context, area, isActive) {
  const center = area.center;
  const radius =
    typeof area.visualRadius === 'number' ? area.visualRadius : Math.min(area.w, area.h) / 2;

  // 背景圆：与旋转手柄风格一致
  context.fillStyle = isActive ? '#8b3eff' : '#ffffff';
  context.strokeStyle = isActive ? '#8b3eff' : '#e2e8f0';
  context.lineWidth = 1;

  // 添加微妙阴影提升层次感
  context.shadowColor = 'rgba(0, 0, 0, 0.08)';
  context.shadowBlur = 4;
  context.shadowOffsetY = 2;

  context.beginPath();
  context.arc(center.x, center.y, radius, 0, 2 * Math.PI);
  context.fill();
  context.stroke();

  // 清除阴影
  context.shadowColor = 'transparent';
  context.shadowBlur = 0;
  context.shadowOffsetY = 0;

  // 内部 icon：上箭头 + 中横线 + 下箭头
  const iconScale = (radius * 2) / 24; // 以 24px 规范图标缩放
  const stroke = isActive ? '#fff' : '#64748b';
  context.strokeStyle = stroke;
  context.lineWidth = 2;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  // 计算局部坐标
  const cx = center.x;
  const cy = center.y;
  const upY = cy - 6 * iconScale;
  const downY = cy + 6 * iconScale;
  const midL = cx - 6 * iconScale;
  const midR = cx + 6 * iconScale;

  // 上箭头（竖+箭头）
  context.beginPath();
  context.moveTo(cx, cy - 1 * iconScale);
  context.lineTo(cx, upY);
  // 箭头两翼
  context.moveTo(cx, upY);
  context.lineTo(cx - 3 * iconScale, upY + 3 * iconScale);
  context.moveTo(cx, upY);
  context.lineTo(cx + 3 * iconScale, upY + 3 * iconScale);
  context.stroke();

  // 中间水平线
  context.beginPath();
  context.moveTo(midL, cy);
  context.lineTo(midR, cy);
  context.stroke();

  // 下箭头（竖+箭头）
  context.beginPath();
  context.moveTo(cx, cy + 1 * iconScale);
  context.lineTo(cx, downY);
  // 箭头两翼
  context.moveTo(cx, downY);
  context.lineTo(cx - 3 * iconScale, downY - 3 * iconScale);
  context.moveTo(cx, downY);
  context.lineTo(cx + 3 * iconScale, downY - 3 * iconScale);
  context.stroke();
}

// 拖动状态缓存
function buildSectionState(stageContext, node, point) {
  const parent = stageContext.api.getNode(node.parentId);
  const children = stageContext.api.getChildren(parent?.id) || [];
  const rectMap = new Map();
  children.forEach((n) => {
    rectMap.set(n.id, stageContext.api.getNodeRegion(n).rect);
  });
  const selectedRect = rectMap.get(node.id);
  const bottomAtStart = selectedRect.y + selectedRect.h;

  // 后续兄弟：top >= selected.bottomAtStart
  const subsequent = children
    .filter((n) => n.id !== node.id)
    .filter((n) => n.locked !== true)
    .filter((n) => {
      const r = rectMap.get(n.id);
      return r.y >= bottomAtStart - 0.0001;
    })
    .sort((a, b) => rectMap.get(a.id).y - rectMap.get(b.id).y);

  // 前一兄弟：bottom 最接近且 <= selected.top
  const prev = (() => {
    const top = selectedRect.y;
    let c = null;
    let best = -Infinity;
    children.forEach((n) => {
      if (n.id === node.id) return;
      const r = rectMap.get(n.id);
      const bottom = r.y + r.h;
      if (bottom <= top && bottom > best) {
        best = bottom;
        c = { id: n.id, bottom };
      }
    });
    return c;
  })();

  const state = {
    nodeId: node.id,
    parentId: parent?.id || null,
    pointStart: { x: point.x, y: point.y },
    selectedStartY: node.y,
    selectedRect,
    bottomAtStart,
    subsequent: subsequent.map((n) => ({ id: n.id, startY: n.y })),
    childrenInitialRects: Object.fromEntries(
      Array.from(rectMap.entries()).map(([id, r]) => [id, { x: r.x, y: r.y, w: r.w, h: r.h }])
    ),
    parentInitialH: parent?.h,
    prevBottom: prev ? prev.bottom : null,
    alignCache: null,
  };
  return state;
}

// 多选场景：构建区段状态（以多选联合外接矩形为基准）
function buildSelectionSectionState(stageContext, selectedNodes, region, point) {
  if (!selectedNodes || selectedNodes.length < 2) return null;
  const parentId = selectedNodes[0]?.parentId;
  const parent = stageContext.api.getNode(parentId);
  const children = stageContext.api.getChildren(parentId) || [];
  const rectMap = new Map();
  children.forEach((n) => {
    rectMap.set(n.id, stageContext.api.getNodeRegion(n).rect);
  });

  // 选区的底边
  const selectedIds = new Set(selectedNodes.map((n) => n.id));
  const bottomAtStart = region.rect.y + region.rect.h;

  const subsequent = children
    .filter((n) => !selectedIds.has(n.id))
    .filter((n) => n.locked !== true)
    .filter((n) => {
      const r = rectMap.get(n.id);
      return r.y >= bottomAtStart - 0.0001;
    })
    .sort((a, b) => rectMap.get(a.id).y - rectMap.get(b.id).y);

  // 前一兄弟：在选区上方且最接近
  const prev = (() => {
    const top = region.rect.y;
    let c = null;
    let best = -Infinity;
    children.forEach((n) => {
      if (selectedIds.has(n.id)) return;
      const r = rectMap.get(n.id);
      const bottom = r.y + r.h;
      if (bottom <= top && bottom > best) {
        best = bottom;
        c = { id: n.id, bottom };
      }
    });
    return c;
  })();

  // 记录每个被选中节点的起始 y
  const selectedStartYs = {};
  selectedNodes.forEach((n) => {
    selectedStartYs[n.id] = n.y;
  });

  return {
    mode: 'selection',
    parentId,
    selectedIds: Array.from(selectedIds),
    selectedStartYs,
    pointStart: { x: point.x, y: point.y },
    selectedRect: { ...region.rect },
    bottomAtStart,
    subsequent: subsequent.map((n) => ({ id: n.id, startY: n.y })),
    childrenInitialRects: Object.fromEntries(
      Array.from(rectMap.entries()).map(([id, r]) => [id, { x: r.x, y: r.y, w: r.w, h: r.h }])
    ),
    parentInitialH: parent?.h,
    prevBottom: prev ? prev.bottom : null,
    alignCache: null,
  };
}

// 父容器尺寸更新（支持增长与收缩到内容）
function resizeParentIfNeeded(stageContext, state, dy) {
  if (!state.parentId) return;
  const parent = stageContext.api.getNode(state.parentId);
  if (!parent) return;

  // 基准高度：优先使用记录的初始 h；若为空（可能为 Hug），退化为区域高度
  let baseH = state.parentInitialH;
  if (!Number.isFinite(baseH)) {
    const pr = stageContext.api.getNodeRegion(parent).rect;
    baseH = pr.h;
  }

  if (dy >= 0) {
    // 向下拖动：父容器高度按位移量同步增加，保持原有底部边距
    const newH = Math.max(0, Math.round(baseH + dy));
    stageContext.api.updateNode(parent.id, { h: newH });
  } else {
    // 向上拖动：父容器高度按位移量同步减少，保持原有底部边距，不按内容回算（避免 hug 效果）
    const newH = Math.max(0, Math.round(baseH + dy));
    stageContext.api.updateNode(parent.id, { h: newH });
  }
}

export function sectionMoveAction(hitTestContext) {
  const { type, targetHandler, stageContext } = hitTestContext;
  const provider = hitTestContext.provider;

  // hover：显示指针/高亮
  if (
    type === EventTypes.move &&
    targetHandler &&
    targetHandler.type === 'sectionMove' &&
    !provider.movingHandler
  ) {
    provider.cursor.set('ns-resize');
    provider.hoverHandlerId = targetHandler.id;
    stageContext.api.markNeedRender();
    return true;
  }

  // mousedown：开始
  if (type === EventTypes.down && targetHandler && targetHandler.type === 'sectionMove') {
    const node = targetHandler.node;
    provider.movingHandler = targetHandler;
    provider.hoverHandlerId = targetHandler.id;
    provider.pointStart = { ...hitTestContext.point };
    if (targetHandler.mode === 'selection') {
      const selNodes = (hitTestContext.selectedNodes || []).filter((n) =>
        targetHandler.selectedIds?.includes(n.id)
      );
      const selRegion = targetHandler.selectionRect
        ? { rect: targetHandler.selectionRect }
        : { rect: hitTestContext.stageContext.api.getSelectedNodesRegion() };
      provider.sectionMoveState = buildSelectionSectionState(
        stageContext,
        selNodes,
        selRegion,
        hitTestContext.point
      );
    } else {
      if (!node) return true;
      provider.sectionMoveState = buildSectionState(stageContext, node, hitTestContext.point);
    }
    stageContext.api.markNeedRender();
    return true;
  }

  // move：拖动
  if (
    type === EventTypes.move &&
    provider.movingHandler &&
    provider.movingHandler.type === 'sectionMove'
  ) {
    const state = provider.sectionMoveState;
    if (!state) return true;
    const node = provider.movingHandler.node;
    // 计算原始 dy（仅纵向）
    const moveY = Math.round(hitTestContext.point.y - state.pointStart.y);

    // 选区/节点参与吸附
    const movingBox = new Rect(
      state.selectedRect.x,
      state.selectedRect.y + moveY,
      state.selectedRect.w,
      state.selectedRect.h
    );
    const movingNodes =
      state.mode === 'selection'
        ? (hitTestContext.selectedNodes || []).map((n) => ({ node: n }))
        : [{ node }];
    if (!state.alignCache) {
      state.alignCache = createAlignCache(
        stageContext,
        movingBox,
        movingNodes,
        0,
        moveY,
        null,
        'move'
      );
    }
    const alignRes = createAlignResult(
      stageContext,
      movingBox,
      movingNodes,
      0,
      moveY,
      null,
      'move',
      state.alignCache
    );
    let adjustedDY = alignRes?.delta?.top ?? moveY;

    // 向上夹制：不越过前兄弟底部（默认最小间距 0）
    if (typeof state.prevBottom === 'number') {
      const newTop = state.selectedRect.y + adjustedDY;
      const minTop = state.prevBottom;
      if (newTop < minTop) {
        adjustedDY = minTop - state.selectedRect.y;
      }
    }

    // 执行批量更新
    stageContext.api.transact(() => {
      if (state.mode === 'selection') {
        // 移动所有选中节点
        for (const id of state.selectedIds) {
          const startY = state.selectedStartYs[id];
          if (typeof startY === 'number') {
            stageContext.api.updateNode(id, { y: startY + adjustedDY });
          }
        }
      } else {
        // 移动选中节点
        stageContext.api.updateNode(state.nodeId, {
          y: state.selectedStartY + adjustedDY,
        });
      }
      // 移动后续兄弟
      for (const s of state.subsequent) {
        stageContext.api.updateNode(s.id, { y: s.startY + adjustedDY });
      }
      // 更新父容器高度
      resizeParentIfNeeded(stageContext, state, adjustedDY);
    });

    provider.cursor.set('ns-resize');
    stageContext.hooks.onNodeMoveCallback();
    stageContext.api.markNeedRender();
    return true;
  }

  // mouseup：结束
  if (
    type === EventTypes.up &&
    provider.movingHandler &&
    provider.movingHandler.type === 'sectionMove'
  ) {
    provider.skipSelectFromUp = true;
    provider.movingHandler = null;
    provider.sectionMoveState = null;
    stageContext.api.markNeedRender();
    return true;
  }

  return false;
}
